// frontend/src/pages/ISP.jsx
import { useState, useEffect, useCallback } from 'react';
import {ispAPI, ispPackageAPI, captivePortalAPI} from '../services/api';
import { useAuth } from '../hooks/useAuth';
import {
  Card, Button, Badge, StatCard, Modal, Input, Select,
  Spinner, Empty, Table, Td, SectionHeader,
} from '../components/ui';
import toast from 'react-hot-toast';
import { clsx } from 'clsx';
import { Plus, Wifi, WifiOff, RefreshCw } from 'lucide-react';

export default function ISP() {
  const { isAdmin, isISP } = useAuth();
  const canManage = isAdmin || isISP;

  const [tab, setTab] = useState('overview');
  const [analytics, setAnalytics] = useState(null);
  const [routers, setRouters] = useState([]);
  const [plans, setPlans] = useState([]);
  const [vouchers, setVouchers] = useState([]);
  const [subscribers, setSubscribers] = useState([]);
  const [loading, setLoading] = useState(true);

  // Add Router modal
  const [addRouterModal, setAddRouterModal] = useState(false);
  const [routerForm, setRouterForm] = useState({ name: '', ipAddress: '', location: '', bandwidth: '' });
  const [routerSaving, setRouterSaving] = useState(false);

  // Generate vouchers modal
  const [genVoucherModal, setGenVoucherModal] = useState(false);
  const [voucherForm, setVoucherForm] = useState({ routerId: '', planId: '', quantity: 10 });
  const [voucherSaving, setVoucherSaving] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const promises = [
        ispAPI.getRouters(),
        ispAPI.getPlans(),
        ispAPI.getVouchers({ limit: 50 }),
        ispAPI.getSubscribers({ limit: 50 }),
      ];
      if (canManage) promises.push(ispAPI.getAnalytics());

      const results = await Promise.allSettled(promises);

      setRouters(results[0].status === 'fulfilled' ? results[0].value.routers || [] : []);
      setPlans(results[1].status === 'fulfilled' ? results[1].value.plans || [] : []);
      setVouchers(results[2].status === 'fulfilled' ? results[2].value.vouchers || [] : []);
      setSubscribers(results[3].status === 'fulfilled' ? results[3].value.subscribers || [] : []);
      if (canManage && results[4]?.status === 'fulfilled') {
        setAnalytics(results[4].value.analytics || null);
      }
    } catch {
      toast.error('Failed to load ISP data');
    } finally {
      setLoading(false);
    }
  }, [canManage]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleToggleRouter = async (id) => {
    try {
      const res = await ispAPI.toggleRouter(id);
      toast.success(res.message || 'Router status updated');
      fetchData();
    } catch (err) {
      toast.error(err.message || 'Failed to update router');
    }
  };

  const handleAddRouter = async () => {
    if (!routerForm.name || !routerForm.ipAddress) {
      toast.error('Router name and IP address are required');
      return;
    }
    setRouterSaving(true);
    try {
      await ispAPI.addRouter(routerForm);
      toast.success('Router added successfully');
      setAddRouterModal(false);
      setRouterForm({ name: '', ipAddress: '', location: '', bandwidth: '' });
      fetchData();
    } catch (err) {
      toast.error(err.message || 'Failed to add router');
    } finally {
      setRouterSaving(false);
    }
  };

  const handleGenerateVouchers = async () => {
    if (!voucherForm.routerId || !voucherForm.planId) {
      toast.error('Select both a router and a plan');
      return;
    }
    setVoucherSaving(true);
    try {
      const res = await ispAPI.generateVouchers({
        routerId: voucherForm.routerId,
        planId: voucherForm.planId,
        quantity: parseInt(voucherForm.quantity),
      });
      toast.success(res.message || `${voucherForm.quantity} vouchers generated!`);
      setGenVoucherModal(false);
      fetchData();
    } catch (err) {
      toast.error(err.message || 'Failed to generate vouchers');
    } finally {
      setVoucherSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner size="xl" />
      </div>
    );
  }

  const a = analytics || {};
  const onlineCount = a.routers?.online || routers.filter(r => r.status === 'online').length;
  const activeSubCount = a.subscribers?.active || subscribers.filter(s => s.status === 'active').length;
  const availableVouchers = vouchers.filter(v => v.status === 'available').length;

  return (
    <div className="space-y-6 animate-slide-up">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-black text-text-primary tracking-tight">ISP & Network</h1>
          <p className="text-text-secondary text-sm mt-1">Manage your internet infrastructure, vouchers, and subscribers.</p>
        </div>
        <button onClick={fetchData} className="text-text-muted hover:text-cyan transition-colors p-2 rounded-xl hover:bg-cyan/10" title="Refresh">
          <RefreshCw className="w-4 h-4" />
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <StatCard label="Online Routers"     value={onlineCount}                  sub={`of ${routers.length} total`}     color="#14B8A6" icon="📡" sparkData={[1,1,2,2,2,onlineCount]}/>
        <StatCard label="Active Subscribers" value={activeSubCount}               sub="Paying customers"                  color="#3B82F6" icon="👥" sparkData={[40,44,48,50,54,activeSubCount]}/>
        <StatCard label="Plans Available"    value={plans.filter(p => p.isActive).length} sub="Active internet plans"   color="#8B5CF6" icon="📶"/>
        <StatCard label="Available Vouchers" value={availableVouchers}            sub={`${vouchers.filter(v=>v.status==='sold').length} sold`} color="#00A8DB" icon="🎫"/>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 flex-wrap">
        {[
          ['overview', 'Overview'],
          ['routers', 'Routers'],
          ['plans', 'Plans'],
          ['vouchers', 'Vouchers'],
          ['subscribers', 'Subscribers'],
        ].map(([id, label]) => (
          <button
            key={id}
            onClick={() => setTab(id)}
            className={clsx(
              'px-4 py-2 rounded-xl text-sm font-semibold transition-all',
              tab === id ? 'bg-cyan text-bg' : 'bg-bg-panel text-text-secondary border border-border hover:border-border-hi'
            )}
          >{label}</button>
        ))}
      </div>

      {/* OVERVIEW */}
      {tab === 'overview' && (
        <div className="grid lg:grid-cols-2 gap-4">
          <Card>
            <SectionHeader
              title="Router Status"
              action={canManage && (
                <Button size="sm" onClick={() => setAddRouterModal(true)}>
                  <Plus className="w-3 h-3" /> Add Router
                </Button>
              )}
            />
            {routers.length === 0 ? (
              <Empty icon="📡" title="No routers configured" description={canManage ? 'Add your first MikroTik router to get started.' : 'No routers assigned to your account.'} />
            ) : (
              <div className="space-y-2">
                {routers.slice(0, 6).map((r) => (
                  <div key={r._id} className="flex items-center justify-between p-3 rounded-xl bg-bg-panel border border-border">
                    <div className="flex items-center gap-2.5">
                      {r.status === 'online'
                        ? <Wifi className="w-4 h-4 text-green flex-shrink-0" />
                        : <WifiOff className="w-4 h-4 text-red flex-shrink-0" />
                      }
                      <div>
                        <p className="text-text-primary text-xs font-semibold">{r.name}</p>
                        <p className="text-text-muted" style={{ fontSize: 10 }}>{r.location || r.ipAddress}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge label={r.status} color={r.status === 'online' ? 'green' : r.status === 'disabled' ? 'gray' : 'red'} />
                      {canManage && (
                        <Button variant="ghost" size="xs" onClick={() => handleToggleRouter(r._id)}>
                          {r.isEnabled ? 'Disable' : 'Enable'}
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Card>

          <Card>
            <SectionHeader title="Recent Subscribers" />
            {subscribers.length === 0 ? (
              <Empty icon="👥" title="No subscribers yet" description="Subscribers appear here after voucher redemptions." />
            ) : (
              <div className="space-y-2">
                {subscribers.slice(0, 6).map((s) => (
                  <div key={s._id} className="flex items-center justify-between p-3 rounded-xl bg-bg-panel border border-border">
                    <div>
                      <p className="text-text-primary text-xs font-semibold">{s.user?.name || s.user?.phone || '—'}</p>
                      <p className="text-text-muted" style={{ fontSize: 10 }}>{s.plan?.name || 'Unknown Plan'} · {s.router?.name}</p>
                    </div>
                    <div className="text-right">
                      <Badge label={s.status} color={s.status === 'active' ? 'green' : s.status === 'expired' ? 'red' : 'amber'} />
                      {s.expiryDate && (
                        <p className="text-text-muted mt-0.5" style={{ fontSize: 10 }}>
                          Exp: {new Date(s.expiryDate).toLocaleDateString()}
                        </p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </div>
      )}

      {/* ROUTERS */}
      {tab === 'routers' && (
        <Card>
          <SectionHeader
            title="Router Management"
            action={canManage && (
              <Button size="sm" onClick={() => setAddRouterModal(true)}>
                <Plus className="w-3 h-3" /> Add Router
              </Button>
            )}
          />
          {routers.length === 0 ? (
            <Empty
              icon="📡"
              title="No routers configured"
              description="Add your MikroTik router to start managing internet access."
              action={canManage ? <Button onClick={() => setAddRouterModal(true)}>Add Router</Button> : undefined}
            />
          ) : (
            <Table headers={['Name', 'IP Address', 'Location', 'Bandwidth', 'Status', ...(canManage ? ['Actions'] : [])]}>
              {routers.map((r) => (
                <tr key={r._id}>
                  <Td className="font-semibold text-text-primary">{r.name}</Td>
                  <Td className="font-mono text-xs">{r.ipAddress}</Td>
                  <Td>{r.location || r.site || '—'}</Td>
                  <Td>{r.bandwidth || '—'}</Td>
                  <Td><Badge label={r.status} color={r.status === 'online' ? 'green' : r.status === 'disabled' ? 'gray' : 'red'} /></Td>
                  {canManage && (
                    <Td>
                      <Button variant="ghost" size="xs" onClick={() => handleToggleRouter(r._id)}>
                        {r.isEnabled ? 'Disable' : 'Enable'}
                      </Button>
                    </Td>
                  )}
                </tr>
              ))}
            </Table>
          )}
        </Card>
      )}

      {/* PLANS */}
      {tab === 'plans' && (
        <Card>
          <SectionHeader title="Internet Plans" />
          {plans.length === 0 ? (
            <Empty icon="📶" title="No plans configured" description="Internet plans define what subscribers can purchase." />
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
              {plans.map((p) => (
                <div key={p._id} className="p-4 rounded-xl bg-bg-panel border border-border" style={{ borderColor: p.isActive ? `${p.color || '#00A8DB'}40` : undefined }}>
                  <div className="flex items-start justify-between mb-2">
                    <p className="text-text-primary font-bold text-sm">{p.name}</p>
                    {p.badge && <Badge label={p.badge} color="cyan" />}
                  </div>
                  <p className="font-black text-xl" style={{ color: p.color || '#00A8DB' }}>
                    UGX {(p.priceUGX || 0).toLocaleString()}
                  </p>
                  {p.priceUSD && <p className="text-text-muted text-xs">≈ ${p.priceUSD}</p>}
                  <div className="mt-2 space-y-1">
                    <p className="text-text-secondary text-xs">⏱ {p.durationDays} days</p>
                    {p.downloadSpeed && <p className="text-text-secondary text-xs">⬇ {p.downloadSpeed} / ⬆ {p.uploadSpeed || p.downloadSpeed}</p>}
                    {p.dataLimitMB ? (
                      <p className="text-text-secondary text-xs">📦 {(p.dataLimitMB / 1024).toFixed(1)} GB data</p>
                    ) : (
                      <p className="text-text-secondary text-xs">♾ Unlimited data</p>
                    )}
                    {p.devicesAllowed > 1 && <p className="text-text-secondary text-xs">📱 Up to {p.devicesAllowed} devices</p>}
                  </div>
                  <div className="mt-2">
                    <Badge label={p.isActive ? 'Active' : 'Inactive'} color={p.isActive ? 'green' : 'gray'} />
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>
      )}

      {/* VOUCHERS */}
      {tab === 'vouchers' && (
        <Card>
          <SectionHeader
            title="Vouchers"
            action={canManage && (
              <Button size="sm" onClick={() => setGenVoucherModal(true)}>
                <Plus className="w-3 h-3" /> Generate
              </Button>
            )}
          />
          {vouchers.length === 0 ? (
            <Empty
              icon="🎫"
              title="No vouchers"
              description="Generate vouchers to sell internet access to customers."
              action={canManage ? <Button onClick={() => setGenVoucherModal(true)}>Generate Vouchers</Button> : undefined}
            />
          ) : (
            <Table headers={['Code', 'Plan', 'Duration', 'Price', 'Status', 'Router']}>
              {vouchers.map((v) => (
                <tr key={v._id}>
                  <Td><code className="text-cyan text-xs font-mono tracking-widest">{v.code}</code></Td>
                  <Td>{v.plan?.name || '—'}</Td>
                  <Td className="text-xs">{v.plan?.durationDays ? `${v.plan.durationDays} days` : '—'}</Td>
                  <Td className="text-xs">UGX {(v.priceUGX || 0).toLocaleString()}</Td>
                  <Td>
                    <Badge
                      label={v.status}
                      color={v.status === 'available' ? 'green' : v.status === 'sold' ? 'amber' : 'red'}
                    />
                  </Td>
                  <Td className="text-xs">{v.router?.name || '—'}</Td>
                </tr>
              ))}
            </Table>
          )}
        </Card>
      )}

      {/* SUBSCRIBERS */}
      {tab === 'subscribers' && (
        <Card>
          <SectionHeader title="Subscribers" />
          {subscribers.length === 0 ? (
            <Empty icon="👥" title="No subscribers yet" description="Subscribers appear here when customers redeem vouchers." />
          ) : (
            <Table headers={['Name', 'Phone', 'Plan', 'Router', 'Status', 'Expires']}>
              {subscribers.map((s) => (
                <tr key={s._id}>
                  <Td className="font-semibold text-text-primary">{s.user?.name || '—'}</Td>
                  <Td>{s.user?.phone || '—'}</Td>
                  <Td>{s.plan?.name || '—'}</Td>
                  <Td>{s.router?.name || '—'}</Td>
                  <Td>
                    <Badge label={s.status} color={s.status === 'active' ? 'green' : s.status === 'expired' ? 'red' : 'amber'} />
                  </Td>
                  <Td className="text-xs text-text-muted">
                    {s.expiryDate ? new Date(s.expiryDate).toLocaleDateString() : '—'}
                  </Td>
                </tr>
              ))}
            </Table>
          )}
        </Card>
      )}

      {/* ADD ROUTER MODAL */}
      <Modal open={addRouterModal} onClose={() => setAddRouterModal(false)} title="Add Router" size="sm">
        <div className="space-y-3">
          <Input
            label="Router Name"
            placeholder="Site A — Main Router"
            value={routerForm.name}
            onChange={(e) => setRouterForm(f => ({ ...f, name: e.target.value }))}
          />
          <Input
            label="IP Address"
            placeholder="192.168.1.1"
            value={routerForm.ipAddress}
            onChange={(e) => setRouterForm(f => ({ ...f, ipAddress: e.target.value }))}
          />
          <Input
            label="Location"
            placeholder="Kalerwe, Kampala"
            value={routerForm.location}
            onChange={(e) => setRouterForm(f => ({ ...f, location: e.target.value }))}
          />
          <Input
            label="Bandwidth"
            placeholder="100 Mbps"
            value={routerForm.bandwidth}
            onChange={(e) => setRouterForm(f => ({ ...f, bandwidth: e.target.value }))}
          />
          <Button className="w-full" loading={routerSaving} onClick={handleAddRouter}>
            Add Router
          </Button>
        </div>
      </Modal>

      {/* GENERATE VOUCHERS MODAL */}
      <Modal open={genVoucherModal} onClose={() => setGenVoucherModal(false)} title="Generate Vouchers" size="sm">
        <div className="space-y-3">
          <Select
            label="Router"
            value={voucherForm.routerId}
            onChange={(e) => setVoucherForm(f => ({ ...f, routerId: e.target.value }))}
          >
            <option value="">Select router...</option>
            {routers.map((r) => (
              <option key={r._id} value={r._id}>{r.name} — {r.location || r.ipAddress}</option>
            ))}
          </Select>
          <Select
            label="Internet Plan"
            value={voucherForm.planId}
            onChange={(e) => setVoucherForm(f => ({ ...f, planId: e.target.value }))}
          >
            <option value="">Select plan...</option>
            {plans.map((p) => (
              <option key={p._id} value={p._id}>{p.name} — UGX {(p.priceUGX || 0).toLocaleString()} / {p.durationDays}d</option>
            ))}
          </Select>
          <Input
            label="Quantity"
            type="number"
            min="1"
            max="200"
            value={voucherForm.quantity}
            onChange={(e) => setVoucherForm(f => ({ ...f, quantity: e.target.value }))}
            note="Max 200 per batch"
          />
          <Button className="w-full" loading={voucherSaving} onClick={handleGenerateVouchers}>
            Generate {voucherForm.quantity} Vouchers
          </Button>
        </div>
      </Modal>
    </div>
  );
}


// ── Phase 49 ISP Admin Tabs (appended) ────────────────────────────────────────

export function IspPackagesAdminTab() {
  const [packages, setPackages] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [form, setForm]         = useState({
    name: '', description: '', durationLabel: '', durationHours: 24, priceUGX: '',
    speedInfo: '', badge: '', providerId: 'manual_voucher', sortOrder: 0,
    type: 'hotspot', maxDevices: 1, category: 'standard', hidden: false,
  });
  const [creating, setCreating] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [msg, setMsg]           = useState('');

  // ── Edit modal — full-field edit, packageId always excluded from what's sent ──
  const [editingPkg, setEditingPkg] = useState(null); // the package row being edited (holds packageId for read-only display)
  const [editForm, setEditForm]     = useState(null);
  const [editErrors, setEditErrors] = useState({});
  const [editSaving, setEditSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await ispPackageAPI.adminList();
      setPackages(r.packages || []);
    } catch (e) {
      setMsg(e.response?.data?.message || e.message);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const handleCreate = async (e) => {
    e.preventDefault();
    setCreating(true);
    setMsg('');
    try {
      await ispPackageAPI.create(form);
      setMsg('Package created');
      setShowForm(false);
      setForm({ name:'', description:'', durationLabel:'', durationHours:24, priceUGX:'', speedInfo:'', badge:'', providerId:'manual_voucher', sortOrder:0, type:'hotspot', maxDevices:1, category:'standard', hidden:false });
      load();
    } catch (e) {
      setMsg(e.response?.data?.message || e.message);
    } finally { setCreating(false); }
  };

  const toggle = async (pkg) => {
    try {
      await ispPackageAPI.update(pkg._id, { isActive: !pkg.isActive });
      load();
    } catch (e) { setMsg(e.message); }
  };

  const openEdit = (pkg) => {
    setEditingPkg(pkg);
    setEditForm({
      name: pkg.name || '',
      description: pkg.description || '',
      durationLabel: pkg.durationLabel || '',
      durationHours: pkg.durationHours ?? '',
      priceUGX: pkg.priceUGX ?? '',
      speedInfo: pkg.speedInfo || '',
      badge: pkg.badge || '',
      sortOrder: pkg.sortOrder ?? 0,
      type: pkg.type || 'hotspot',
      providerId: pkg.providerId || 'manual_voucher',
      maxDevices: pkg.maxDevices ?? 1,
      category: pkg.category || 'standard',
      hidden: pkg.hidden === true,
      redemptionInstructions: pkg.redemptionInstructions || '',
      isActive: pkg.isActive !== false,
    });
    setEditErrors({});
  };

  const closeEdit = () => {
    setEditingPkg(null);
    setEditForm(null);
    setEditErrors({});
  };

  const validateEditForm = (f) => {
    const errors = {};
    if (!f.name.trim()) errors.name = 'Name is required';
    if (!f.durationLabel.trim()) errors.durationLabel = 'Duration label is required';
    const price = Number(f.priceUGX);
    if (f.priceUGX === '' || Number.isNaN(price) || price < 0) errors.priceUGX = 'Price must be 0 or more';
    const hours = Number(f.durationHours);
    if (f.durationHours === '' || Number.isNaN(hours) || hours <= 0) errors.durationHours = 'Duration must be greater than 0';
    const devices = Number(f.maxDevices);
    if (f.maxDevices === '' || Number.isNaN(devices) || devices < 1) errors.maxDevices = 'Must allow at least 1 device';
    return errors;
  };

  const handleEditSave = async (e) => {
    e.preventDefault();
    const errors = validateEditForm(editForm);
    setEditErrors(errors);
    if (Object.keys(errors).length > 0) return;

    setEditSaving(true);
    try {
      // Explicit whitelist — packageId is NEVER included here, regardless of
      // what's in editForm/state. It's shown read-only in the modal for
      // reference only — it's a stable identifier used elsewhere (e.g.
      // seed idempotency keys), so it must never change after creation.
      const payload = {
        name: editForm.name.trim(),
        description: editForm.description,
        durationLabel: editForm.durationLabel.trim(),
        durationHours: Number(editForm.durationHours),
        priceUGX: Number(editForm.priceUGX),
        speedInfo: editForm.speedInfo || null,
        badge: editForm.badge || null,
        sortOrder: Number(editForm.sortOrder) || 0,
        type: editForm.type,
        providerId: editForm.providerId,
        maxDevices: Number(editForm.maxDevices),
        category: editForm.category === 'family' ? 'family' : 'standard',
        hidden: !!editForm.hidden,
        redemptionInstructions: editForm.redemptionInstructions,
        isActive: !!editForm.isActive,
      };
      await ispPackageAPI.update(editingPkg._id, payload);
      toast.success('Package updated');
      closeEdit();
      load();
    } catch (err) {
      toast.error(err.message || 'Failed to update package');
    } finally {
      setEditSaving(false);
    }
  };

  const seedHotspot = async () => {
    try {
      const r = await captivePortalAPI.seedPackages();
      setMsg(r.created?.length ? `Seeded: ${r.created.map(p => p.name).join(', ')}` : 'Hotspot packages already exist — nothing to seed');
      load();
    } catch (e) { setMsg(e.response?.data?.message || e.message); }
  };

  return (
    <div style={{ padding: 16 }}>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:12, gap:8, flexWrap:'wrap' }}>
        <h3 style={{ margin:0 }}>ISP Packages</h3>
        <div style={{ display:'flex', gap:8 }}>
          <button onClick={seedHotspot} style={{ padding:'6px 14px', borderRadius:6,
            background:'transparent', color:'var(--text-primary)', fontWeight:600, border:'1px solid var(--border)', cursor:'pointer' }}>
            Seed DWIFI Walk-Up Packages
          </button>
          <button onClick={() => setShowForm(v => !v)} style={{ padding:'6px 14px', borderRadius:6,
            background:'#22d3ee', color:'#0f172a', fontWeight:700, border:'none', cursor:'pointer' }}>
            {showForm ? 'Cancel' : '+ New Package'}
          </button>
        </div>
      </div>

      {msg && <div style={{ background:'#fef9c3', color:'#78350f', padding:'8px 12px', borderRadius:6, marginBottom:12, fontSize:13 }}>{msg}</div>}

      {showForm && (
        <form onSubmit={handleCreate} style={{ background:'var(--bg-card)', padding:16, borderRadius:10, border:'1px solid var(--border)', marginBottom:16 }}>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10 }}>
            {[['name','Name*'],['durationLabel','Duration Label*'],['priceUGX','Price UGX*'],['durationHours','Duration Hours'],['speedInfo','Speed Info'],['badge','Badge'],['sortOrder','Sort Order']].map(([k,l]) => (
              <div key={k}>
                <label style={{ fontSize:12, color:'var(--text-muted)', display:'block', marginBottom:3 }}>{l}</label>
                <input value={form[k]} onChange={e => setForm(f => ({...f, [k]: e.target.value}))}
                  style={{ width:'100%', padding:'7px 10px', borderRadius:6, border:'1px solid var(--border)', background:'var(--bg-page)', color:'var(--text-primary)', boxSizing:'border-box' }} />
              </div>
            ))}
            <div>
              <label style={{ fontSize:12, color:'var(--text-muted)', display:'block', marginBottom:3 }}>Provider</label>
              <select value={form.providerId} onChange={e => setForm(f => ({...f, providerId: e.target.value}))}
                style={{ width:'100%', padding:'7px 10px', borderRadius:6, border:'1px solid var(--border)', background:'var(--bg-page)', color:'var(--text-primary)' }}>
                <option value="manual_voucher">Manual Voucher</option>
                <option value="centipid_api">Centipid API (stub)</option>
              </select>
            </div>
            <div>
              <label style={{ fontSize:12, color:'var(--text-muted)', display:'block', marginBottom:3 }}>Type</label>
              <select value={form.type} onChange={e => setForm(f => ({...f, type: e.target.value}))}
                style={{ width:'100%', padding:'7px 10px', borderRadius:6, border:'1px solid var(--border)', background:'var(--bg-page)', color:'var(--text-primary)' }}>
                <option value="hotspot">Hotspot (captive portal / walk-up)</option>
                <option value="pppoe">PPPoE</option>
                <option value="data">Data</option>
              </select>
            </div>
            <div>
              <label style={{ fontSize:12, color:'var(--text-muted)', display:'block', marginBottom:3 }}>Max Devices</label>
              <input type="number" min="1" value={form.maxDevices} onChange={e => setForm(f => ({...f, maxDevices: e.target.value}))}
                style={{ width:'100%', padding:'7px 10px', borderRadius:6, border:'1px solid var(--border)', background:'var(--bg-page)', color:'var(--text-primary)', boxSizing:'border-box' }} />
            </div>
            <div>
              <label style={{ fontSize:12, color:'var(--text-muted)', display:'block', marginBottom:3 }}>Category</label>
              <select value={form.category} onChange={e => setForm(f => ({...f, category: e.target.value}))}
                style={{ width:'100%', padding:'7px 10px', borderRadius:6, border:'1px solid var(--border)', background:'var(--bg-page)', color:'var(--text-primary)' }}>
                <option value="standard">Standard (time-based)</option>
                <option value="family">Family (multi-device)</option>
              </select>
            </div>
            <div>
              <label style={{ fontSize:12, color:'var(--text-muted)', display:'block', marginBottom:3 }}>Visibility</label>
              <select value={form.hidden ? 'hidden' : 'public'} onChange={e => setForm(f => ({...f, hidden: e.target.value === 'hidden'}))}
                style={{ width:'100%', padding:'7px 10px', borderRadius:6, border:'1px solid var(--border)', background:'var(--bg-page)', color:'var(--text-primary)' }}>
                <option value="public">Public — shows on customer portal</option>
                <option value="hidden">Hidden — not listed, not purchasable there</option>
              </select>
            </div>
          </div>
          <div style={{ marginTop:10 }}>
            <label style={{ fontSize:12, color:'var(--text-muted)', display:'block', marginBottom:3 }}>Description</label>
            <textarea value={form.description} onChange={e => setForm(f => ({...f, description: e.target.value}))}
              style={{ width:'100%', padding:'7px 10px', borderRadius:6, border:'1px solid var(--border)', background:'var(--bg-page)', color:'var(--text-primary)', boxSizing:'border-box', minHeight:60 }} />
          </div>
          <div style={{ marginTop:10 }}>
            <label style={{ fontSize:12, color:'var(--text-muted)', display:'block', marginBottom:3 }}>Redemption Instructions</label>
            <textarea value={form.redemptionInstructions || ''} onChange={e => setForm(f => ({...f, redemptionInstructions: e.target.value}))}
              style={{ width:'100%', padding:'7px 10px', borderRadius:6, border:'1px solid var(--border)', background:'var(--bg-page)', color:'var(--text-primary)', boxSizing:'border-box', minHeight:50 }} />
          </div>
          <button type="submit" disabled={creating} style={{ marginTop:12, padding:'8px 20px', borderRadius:7,
            background:'#22d3ee', color:'#0f172a', fontWeight:700, border:'none', cursor:'pointer' }}>
            {creating ? 'Creating…' : 'Create Package'}
          </button>
        </form>
      )}

      {loading ? <p style={{ color:'var(--text-muted)' }}>Loading…</p> : (
        <table style={{ width:'100%', borderCollapse:'collapse', fontSize:13 }}>
          <thead>
            <tr style={{ background:'var(--bg-page)' }}>
              {['Name','Type','Category','Duration','Price UGX','Devices','Provider','Stock','Status','Visibility',''].map(h => (
                <th key={h} style={{ padding:'7px 10px', textAlign:'left', color:'var(--text-muted)', fontWeight:600, fontSize:11, textTransform:'uppercase', letterSpacing:'0.04em' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {packages.map(pkg => (
              <tr key={pkg._id} style={{ borderBottom:'1px solid var(--border)' }}>
                <td style={{ padding:'8px 10px', fontWeight:600 }}>{pkg.name}</td>
                <td style={{ padding:'8px 10px' }}>{pkg.type}</td>
                <td style={{ padding:'8px 10px' }}>
                  <span style={{ background: pkg.category === 'family' ? '#ede9fe' : '#e0f2fe', color: pkg.category === 'family' ? '#5b21b6' : '#075985',
                    padding:'2px 8px', borderRadius:99, fontSize:11, fontWeight:700 }}>
                    {pkg.category === 'family' ? 'Family' : 'Standard'}
                  </span>
                </td>
                <td style={{ padding:'8px 10px' }}>{pkg.durationLabel}</td>
                <td style={{ padding:'8px 10px', fontVariantNumeric:'tabular-nums' }}>{(pkg.priceUGX||0).toLocaleString()}</td>
                <td style={{ padding:'8px 10px' }}>{pkg.maxDevices ?? 1}</td>
                <td style={{ padding:'8px 10px' }}>{pkg.providerId}</td>
                <td style={{ padding:'8px 10px' }}>{pkg.stock?.available ?? '—'} avail</td>
                <td style={{ padding:'8px 10px' }}>
                  <span style={{ background: pkg.isActive ? '#dcfce7' : '#fee2e2', color: pkg.isActive ? '#166534' : '#991b1b',
                    padding:'2px 8px', borderRadius:99, fontSize:11, fontWeight:700 }}>
                    {pkg.isActive ? 'Active' : 'Inactive'}
                  </span>
                </td>
                <td style={{ padding:'8px 10px' }}>
                  <span style={{ background: pkg.hidden ? '#fef3c7' : '#dcfce7', color: pkg.hidden ? '#92400e' : '#166534',
                    padding:'2px 8px', borderRadius:99, fontSize:11, fontWeight:700 }}>
                    {pkg.hidden ? 'Hidden' : 'Public'}
                  </span>
                </td>
                <td style={{ padding:'8px 10px', display:'flex', gap:6 }}>
                  <button onClick={() => openEdit(pkg)} style={{ padding:'3px 10px', borderRadius:6,
                    background:'transparent', border:'1px solid var(--border)', cursor:'pointer',
                    color:'var(--text-primary)', fontSize:12 }}>
                    Edit
                  </button>
                  <button onClick={() => toggle(pkg)} style={{ padding:'3px 10px', borderRadius:6,
                    background:'transparent', border:'1px solid var(--border)', cursor:'pointer',
                    color:'var(--text-muted)', fontSize:12 }}>
                    {pkg.isActive ? 'Deactivate' : 'Activate'}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {packages.length === 0 && !loading && (
        <p style={{ color:'var(--text-muted)', textAlign:'center', padding:24 }}>No packages yet. Create one above.</p>
      )}

      <Modal open={!!editingPkg} onClose={closeEdit} title={`Edit Package${editingPkg ? ' — ' + editingPkg.name : ''}`} size="lg">
        {editForm && (
          <form onSubmit={handleEditSave} className="flex flex-col gap-4">
            <Input label="Package ID (locked — never editable)" value={editingPkg.packageId} disabled readOnly
              note="Hardcoded elsewhere for package matching (e.g. Family packages) — cannot be changed here." />

            <div className="grid grid-cols-2 gap-4">
              <Input label="Name*" value={editForm.name}
                onChange={e => setEditForm(f => ({ ...f, name: e.target.value }))} error={editErrors.name} />
              <Input label="Price UGX*" type="number" min="0" value={editForm.priceUGX}
                onChange={e => setEditForm(f => ({ ...f, priceUGX: e.target.value }))} error={editErrors.priceUGX} />
              <Input label="Duration Label*" value={editForm.durationLabel}
                onChange={e => setEditForm(f => ({ ...f, durationLabel: e.target.value }))} error={editErrors.durationLabel}
                note='e.g. "4 Hours", "1 Day", "3 Days"' />
              <Input label="Duration Hours*" type="number" min="1" value={editForm.durationHours}
                onChange={e => setEditForm(f => ({ ...f, durationHours: e.target.value }))} error={editErrors.durationHours} />
              <Input label="Max Devices*" type="number" min="1" value={editForm.maxDevices}
                onChange={e => setEditForm(f => ({ ...f, maxDevices: e.target.value }))} error={editErrors.maxDevices} />
              <Input label="Sort Order" type="number" value={editForm.sortOrder}
                onChange={e => setEditForm(f => ({ ...f, sortOrder: e.target.value }))} />
              <Input label="Speed Info" value={editForm.speedInfo}
                onChange={e => setEditForm(f => ({ ...f, speedInfo: e.target.value }))} />
              <Input label="Badge" value={editForm.badge}
                onChange={e => setEditForm(f => ({ ...f, badge: e.target.value }))} />
              <Select label="Type" value={editForm.type}
                onChange={e => setEditForm(f => ({ ...f, type: e.target.value }))}>
                <option value="hotspot">Hotspot (captive portal / walk-up)</option>
                <option value="pppoe">PPPoE</option>
                <option value="data">Data</option>
              </Select>
              <Select label="Provider" value={editForm.providerId}
                onChange={e => setEditForm(f => ({ ...f, providerId: e.target.value }))}>
                <option value="manual_voucher">Manual Voucher</option>
                <option value="centipid_api">Centipid API (stub)</option>
              </Select>
              <Select label="Status" value={editForm.isActive ? 'active' : 'inactive'}
                onChange={e => setEditForm(f => ({ ...f, isActive: e.target.value === 'active' }))}>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </Select>
              <Select label="Visibility" value={editForm.hidden ? 'hidden' : 'public'}
                onChange={e => setEditForm(f => ({ ...f, hidden: e.target.value === 'hidden' }))}>
                <option value="public">Public — shows on customer portal</option>
                <option value="hidden">Hidden — not listed, not purchasable there</option>
              </Select>
              <Select label="Category" value={editForm.category}
                onChange={e => setEditForm(f => ({ ...f, category: e.target.value }))}>
                <option value="standard">Standard (time-based)</option>
                <option value="family">Family (multi-device)</option>
              </Select>
            </div>

            <div className="bg-bg-panel border border-border rounded-xl px-4 py-3 text-xs text-text-muted leading-relaxed">
              <strong className="text-text-secondary">Status</strong> vs <strong className="text-text-secondary">Visibility</strong> — two separate controls:
              <br />• <strong>Inactive</strong> = fully off. Won't show anywhere, can't be purchased by anyone, for any reason (same as a retired/discontinued package).
              <br />• <strong>Hidden</strong> = still a real, active package — but not listed on the customer portal, and not purchasable there either (there's currently no direct-link bypass). Use this to prep a package before a launch, then flip it to Public when ready.
            </div>

            <div className="flex flex-col gap-1.5">
              <label className="text-xs font-bold text-text-muted uppercase tracking-wider">Description</label>
              <textarea value={editForm.description}
                onChange={e => setEditForm(f => ({ ...f, description: e.target.value }))}
                className="w-full bg-bg-panel border border-border rounded-xl px-4 py-3 text-text-primary text-sm outline-none focus:border-cyan min-h-[60px]" />
            </div>
            <div className="flex flex-col gap-1.5">
              <label className="text-xs font-bold text-text-muted uppercase tracking-wider">Redemption Instructions</label>
              <textarea value={editForm.redemptionInstructions}
                onChange={e => setEditForm(f => ({ ...f, redemptionInstructions: e.target.value }))}
                className="w-full bg-bg-panel border border-border rounded-xl px-4 py-3 text-text-primary text-sm outline-none focus:border-cyan min-h-[50px]" />
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="ghost" onClick={closeEdit}>Cancel</Button>
              <Button type="submit" loading={editSaving}>Save Changes</Button>
            </div>
          </form>
        )}
      </Modal>
    </div>
  );
}

export function IspVoucherStockAdminTab() {
  const [packages,    setPackages]    = useState([]);
  const [selPkg,      setSelPkg]      = useState('');
  const [stock,       setStock]       = useState(null);
  const [inputMethod, setInputMethod] = useState('paste');
  const [rawText,     setRawText]     = useState('');
  const [fileName,    setFileName]    = useState('');
  const [parsedCodes, setParsedCodes] = useState([]);
  const [preview,     setPreview]     = useState(null);
  const [previewing,  setPreviewing]  = useState(false);
  const [uploading,   setUploading]   = useState(false);
  const [msg,         setMsg]         = useState({ text: '', type: '' });

  useEffect(() => {
    ispPackageAPI.adminList().then(r => setPackages(r.packages || [])).catch(() => {});
  }, []);

  const loadStock = async (id) => {
    if (!id) return;
    try {
      const r = await ispPackageAPI.getStock(id);
      setStock(r.stock);
    } catch {}
  };

  // CSV / plain-text parser — handles: one-per-line, comma-separated, CSV with headers
  function parseCodes(text) {
    const lines = text.split(/\r?\n/).filter(l => l.trim());
    if (!lines.length) return [];
    const CODE_HEADERS = new Set(['code','voucher','pin','serial','voucher_code','coupon','vouchercode','code#']);
    const firstLine = lines[0];
    // CSV with a recognised header column → extract that column only
    if (firstLine.includes(',')) {
      const hdrs = firstLine.split(',').map(h => h.trim().toLowerCase().replace(/['"]/g, ''));
      const hi = hdrs.findIndex(h => CODE_HEADERS.has(h));
      if (hi >= 0) {
        const codes = [];
        for (let li = 1; li < lines.length; li++) {
          const val = (lines[li].split(',')[hi] || '').trim().replace(/['"]/g, '').toUpperCase();
          if (val) codes.push(val);
        }
        return codes;
      }
    }
    // No recognised header → flatten all comma-separated values across all lines
    const codes = [];
    for (const line of lines) {
      if (line.includes(',')) {
        line.split(',').forEach(c => { const v = c.trim().replace(/['"]/g, '').toUpperCase(); if (v) codes.push(v); });
      } else {
        const v = line.trim().toUpperCase();
        if (v) codes.push(v);
      }
    }
    return codes;
  }

  const onTextChange = (text) => {
    setRawText(text);
    setParsedCodes(parseCodes(text));
    setPreview(null);
  };

  const onFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setFileName(file.name);
    const reader = new FileReader();
    reader.onload = (ev) => {
      const text = ev.target.result || '';
      setParsedCodes(parseCodes(text));
      setRawText(text);
      setPreview(null);
    };
    reader.readAsText(file);
  };

  const handlePreview = async () => {
    if (!selPkg || parsedCodes.length === 0) return;
    setPreviewing(true);
    setMsg({ text: '', type: '' });
    try {
      const r = await ispPackageAPI.previewCodes(parsedCodes);
      setPreview(r);
    } catch (e) {
      setMsg({ text: 'Preview failed: ' + (e.response?.data?.message || e.message), type: 'error' });
    } finally { setPreviewing(false); }
  };

  const handleConfirmUpload = async () => {
    if (!selPkg || parsedCodes.length === 0) return;
    setUploading(true);
    setMsg({ text: '', type: '' });
    try {
      const r = await ispPackageAPI.uploadCodes(selPkg, parsedCodes);
      const added = r.added ?? 0;
      const dups  = r.duplicates ?? 0;
      setMsg({ text: 'Added ' + added + ' code' + (added !== 1 ? 's' : '') + '. ' + dups + ' duplicate' + (dups !== 1 ? 's' : '') + ' skipped.', type: 'success' });
      setRawText('');
      setFileName('');
      setParsedCodes([]);
      setPreview(null);
      loadStock(selPkg);
      ispPackageAPI.adminList().then(r2 => setPackages(r2.packages || [])).catch(() => {});
    } catch (e) {
      setMsg({ text: e.response?.data?.message || e.message || 'Upload failed', type: 'error' });
    } finally { setUploading(false); }
  };

  const S = {
    label:  { fontSize: 12, color: 'var(--text-muted)', display: 'block', marginBottom: 4 },
    sel:    { padding: '8px 12px', borderRadius: 7, border: '1px solid var(--border)', background: 'var(--bg-page)', color: 'var(--text-primary)', minWidth: 260 },
    tabBtn: (active) => ({
      padding: '6px 14px', borderRadius: 6, border: '1px solid var(--border)',
      cursor: 'pointer', fontWeight: 600, fontSize: 12,
      background: active ? '#22d3ee' : 'var(--bg-panel)',
      color: active ? '#0f172a' : 'var(--text-secondary)',
    }),
    ta:     {
      width: '100%', minHeight: 140, padding: '8px 10px', borderRadius: 8,
      boxSizing: 'border-box', border: '1px solid var(--border)',
      background: 'var(--bg-page)', color: 'var(--text-primary)',
      fontFamily: 'monospace', fontSize: 12,
    },
    btn: (disabled, color) => ({
      padding: '9px 20px', borderRadius: 8, fontWeight: 700,
      border: 'none', cursor: disabled ? 'not-allowed' : 'pointer',
      background: disabled ? 'var(--border)' : (color || '#22d3ee'),
      color: disabled ? 'var(--text-muted)' : (color === '#16a34a' ? '#fff' : '#0f172a'),
      opacity: disabled ? 0.6 : 1,
    }),
    msgBox: (type) => ({
      padding: '10px 14px', borderRadius: 8, marginBottom: 12, fontSize: 13,
      background: type === 'success' ? '#dcfce7' : type === 'error' ? '#fee2e2' : '#fef9c3',
      color:      type === 'success' ? '#166534' : type === 'error' ? '#991b1b' : '#78350f',
    }),
    stat: (color) => ({
      display: 'inline-flex', flexDirection: 'column', alignItems: 'center',
      background: color + '22', borderRadius: 8, padding: '6px 14px', minWidth: 72,
      border: '1px solid ' + color + '44',
    }),
  };

  return (
    <div style={{ padding: 16 }}>
      <h3 style={{ marginTop: 0, marginBottom: 16 }}>Voucher Stock</h3>

      {msg.text && <div style={S.msgBox(msg.type)}>{msg.text}</div>}

      <label style={S.label}>Package</label>
      <select
        value={selPkg}
        onChange={e => { setSelPkg(e.target.value); setPreview(null); loadStock(e.target.value); }}
        style={{ ...S.sel, marginBottom: 16 }}
      >
        <option value="">— choose package —</option>
        {packages.map(p => (
          <option key={p._id} value={p._id}>
            {p.name} — {(p.priceUGX || 0).toLocaleString()} UGX
          </option>
        ))}
      </select>

      {stock && (
        <div style={{ display: 'flex', gap: 8, marginBottom: 20, flexWrap: 'wrap' }}>
          {[
            ['Available', stock.available, '#16a34a'],
            ['Reserved',  stock.reserved,  '#d97706'],
            ['Sold',      stock.sold,      '#6366f1'],
            ['Cancelled', stock.cancelled, '#dc2626'],
          ].map(([label, count, color]) => (
            <div key={label} style={S.stat(color)}>
              <span style={{ fontSize: 20, fontWeight: 800, fontVariantNumeric: 'tabular-nums', color }}>{count}</span>
              <span style={{ fontSize: 10, fontWeight: 700, color, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{label}</span>
            </div>
          ))}
        </div>
      )}

      {!selPkg
        ? <p style={{ color: 'var(--text-muted)', fontStyle: 'italic', fontSize: 13 }}>Select a package above to upload voucher codes.</p>
        : (
        <div>
          <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
            <button style={S.tabBtn(inputMethod === 'paste')} onClick={() => { setInputMethod('paste'); setPreview(null); }}>Paste Codes</button>
            <button style={S.tabBtn(inputMethod === 'file')}  onClick={() => { setInputMethod('file');  setPreview(null); }}>Upload File (.csv / .txt)</button>
          </div>

          {inputMethod === 'paste' && (
            <div>
              <label style={S.label}>Paste codes — one per line, comma-separated, or CSV with a header row</label>
              <textarea
                value={rawText}
                onChange={e => onTextChange(e.target.value)}
                placeholder="CODE001&#10;CODE002&#10;— or CSV from Centipid —&#10;code,duration&#10;ABC123,1day"
                style={S.ta}
              />
            </div>
          )}

          {inputMethod === 'file' && (
            <div style={{ marginBottom: 12 }}>
              <label style={S.label}>Select a .csv or .txt batch file from Centipid</label>
              <input
                type="file"
                accept=".csv,.txt,text/csv,text/plain"
                onChange={onFileChange}
                style={{ display: 'block', marginBottom: 8, fontSize: 13, color: 'var(--text-primary)' }}
              />
              {fileName && (
                <p style={{ fontSize: 12, color: 'var(--text-muted)', margin: 0 }}>
                  {fileName} — {parsedCodes.length} code{parsedCodes.length !== 1 ? 's' : ''} detected
                </p>
              )}
            </div>
          )}

          {parsedCodes.length > 0 && !preview && (
            <p style={{ fontSize: 12, color: 'var(--text-muted)', margin: '8px 0' }}>
              <strong style={{ color: 'var(--text-primary)' }}>{parsedCodes.length}</strong> code{parsedCodes.length !== 1 ? 's' : ''} detected — click Preview to check for duplicates.
            </p>
          )}

          {preview && (
            <div style={{ background: 'var(--bg-panel)', border: '1px solid var(--border)', borderRadius: 10, padding: '12px 16px', marginBottom: 12 }}>
              <p style={{ margin: '0 0 10px', fontWeight: 700, fontSize: 13 }}>Upload Preview</p>
              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 8 }}>
                {[
                  ['Will Add',    preview.valid,          '#16a34a'],
                  ['In Stock',    preview.dupInStock,     '#d97706'],
                  ['Batch Dup',   preview.dupWithinBatch, '#6366f1'],
                  ['Invalid',     preview.invalid,        '#dc2626'],
                ].map(([label, count, color]) => (
                  <div key={label} style={S.stat(color)}>
                    <span style={{ fontSize: 18, fontWeight: 800, color }}>{count}</span>
                    <span style={{ fontSize: 10, fontWeight: 700, color, textTransform: 'uppercase' }}>{label}</span>
                  </div>
                ))}
              </div>
              {preview.sampleDuplicates && preview.sampleDuplicates.length > 0 && (
                <p style={{ fontSize: 11, color: 'var(--text-muted)', margin: '6px 0 0' }}>
                  Duplicates: {preview.sampleDuplicates.map(d => d.code + ' [' + d.pkg + ']').join(', ')}
                  {preview.dupInStock > 5 ? ' …and ' + (preview.dupInStock - 5) + ' more' : ''}
                </p>
              )}
              {preview.valid === 0 && (
                <p style={{ fontSize: 12, color: '#dc2626', margin: '8px 0 0' }}>No new codes to add.</p>
              )}
            </div>
          )}

          <div style={{ display: 'flex', gap: 10, marginTop: 12, flexWrap: 'wrap' }}>
            <button
              onClick={handlePreview}
              disabled={previewing || parsedCodes.length === 0}
              style={S.btn(previewing || parsedCodes.length === 0)}
            >
              {previewing ? 'Checking…' : 'Preview'}
            </button>
            {preview && preview.valid > 0 && (
              <button
                onClick={handleConfirmUpload}
                disabled={uploading}
                style={S.btn(uploading, '#16a34a')}
              >
                {uploading ? 'Uploading…' : 'Confirm Upload (' + preview.valid + ' codes)'}
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Monthly-client self-service: Activations ──────────────────────────────────
// Three inner panels: Pending Activations queue, Add Existing Subscriber
// (single form + CSV bulk import), Imported Subscribers list.

function parseSubscriberCsv(text) {
  const lines = text.split(/\r?\n/).filter(l => l.trim());
  if (!lines.length) return [];
  const hdrs = lines[0].split(',').map(h => h.trim().toLowerCase().replace(/['"]/g, ''));
  const idx = {
    phone:       hdrs.indexOf('phone'),
    name:        hdrs.indexOf('name'),
    ispIdentity: hdrs.indexOf('ispidentity'),
    packageName: hdrs.indexOf('packagename'),
    expiryDate:  hdrs.indexOf('expirydate'),
  };
  if (idx.phone < 0 || idx.ispIdentity < 0 || idx.expiryDate < 0) return [];
  const rows = [];
  for (const line of lines.slice(1)) {
    const cols = line.split(',').map(c => c.trim().replace(/['"]/g, ''));
    rows.push({
      phone:       cols[idx.phone] || '',
      name:        idx.name >= 0 ? (cols[idx.name] || '') : '',
      ispIdentity: cols[idx.ispIdentity] || '',
      packageName: idx.packageName >= 0 ? (cols[idx.packageName] || '') : '',
      expiryDate:  cols[idx.expiryDate] || '',
    });
  }
  return rows.filter(r => r.phone);
}

const AS = {
  label:  { fontSize: 12, color: 'var(--text-muted)', display: 'block', marginBottom: 4 },
  sel:    { padding: '8px 12px', borderRadius: 7, border: '1px solid var(--border)', background: 'var(--bg-page)', color: 'var(--text-primary)', minWidth: 200 },
  input:  { padding: '8px 12px', borderRadius: 7, border: '1px solid var(--border)', background: 'var(--bg-page)', color: 'var(--text-primary)', minWidth: 200, boxSizing: 'border-box' },
  innerTabBtn: (active) => ({
    padding: '6px 14px', borderRadius: 6, border: '1px solid var(--border)',
    cursor: 'pointer', fontWeight: 600, fontSize: 12,
    background: active ? '#22d3ee' : 'var(--bg-panel)',
    color: active ? '#0f172a' : 'var(--text-secondary)',
  }),
  ta: {
    width: '100%', minHeight: 120, padding: '8px 10px', borderRadius: 8,
    boxSizing: 'border-box', border: '1px solid var(--border)',
    background: 'var(--bg-page)', color: 'var(--text-primary)',
    fontFamily: 'monospace', fontSize: 12,
  },
  btn: (disabled, color) => ({
    padding: '9px 20px', borderRadius: 8, fontWeight: 700,
    border: 'none', cursor: disabled ? 'not-allowed' : 'pointer',
    background: disabled ? 'var(--border)' : (color || '#22d3ee'),
    color: disabled ? 'var(--text-muted)' : (color === '#16a34a' ? '#fff' : '#0f172a'),
    opacity: disabled ? 0.6 : 1,
  }),
  msgBox: (type) => ({
    padding: '10px 14px', borderRadius: 8, marginBottom: 12, fontSize: 13,
    background: type === 'success' ? '#dcfce7' : type === 'error' ? '#fee2e2' : '#fef9c3',
    color:      type === 'success' ? '#166534' : type === 'error' ? '#991b1b' : '#78350f',
  }),
  row: {
    background: 'var(--bg-panel)', border: '1px solid var(--border)', borderRadius: 10,
    padding: '12px 16px', marginBottom: 10,
  },
  rowUrgent: {
    background: 'var(--bg-panel)', border: '2px solid #dc2626', borderRadius: 10,
    padding: '12px 16px', marginBottom: 10,
  },
  pill: (bg, color) => ({
    display: 'inline-block', background: bg, color, padding: '2px 10px', borderRadius: 99,
    fontSize: 11, fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase', marginLeft: 6,
  }),
};

export function IspActivationsAdminTab() {
  const [inner, setInner] = useState('pending'); // 'pending' | 'add' | 'imported'
  const [msg, setMsg] = useState({ text: '', type: '' });

  // Pending activations
  const [pending, setPending] = useState([]);
  const [pendingLoading, setPendingLoading] = useState(true);
  const [activatingId, setActivatingId] = useState(null);

  const loadPending = useCallback(async () => {
    setPendingLoading(true);
    try {
      const r = await ispPackageAPI.adminActivations();
      setPending(r.purchases || []);
    } catch (e) {
      setMsg({ text: e.response?.data?.message || e.message, type: 'error' });
    } finally { setPendingLoading(false); }
  }, []);

  useEffect(() => { loadPending(); }, [loadPending]);

  const handleActivate = async (purchaseId) => {
    setActivatingId(purchaseId);
    setMsg({ text: '', type: '' });
    try {
      await ispPackageAPI.activatePurchase(purchaseId);
      setMsg({ text: 'Activated. Customer notified.', type: 'success' });
      loadPending();
    } catch (e) {
      setMsg({ text: e.response?.data?.message || e.message || 'Activation failed', type: 'error' });
    } finally { setActivatingId(null); }
  };

  // Add Existing Subscriber — single form
  const [packages, setPackages] = useState([]);
  const [form, setForm] = useState({ phone: '', name: '', ispIdentity: '', packageId: '', expiresAt: '' });
  const [saving, setSaving] = useState(false);
  const [addedCode, setAddedCode] = useState(null); // last-generated reference code, shown to admin to hand off

  useEffect(() => {
    ispPackageAPI.adminList().then(r => setPackages(r.packages || [])).catch(() => {});
  }, []);

  const handleAddSubscriber = async () => {
    if (!form.phone || !form.ispIdentity || !form.expiresAt) {
      setMsg({ text: 'Phone, ispIdentity and expiry date are required', type: 'error' });
      return;
    }
    setSaving(true);
    setMsg({ text: '', type: '' });
    setAddedCode(null);
    try {
      const r = await ispPackageAPI.createImport(form);
      setAddedCode(r.subscription?.referenceCode || null);
      setForm({ phone: '', name: '', ispIdentity: '', packageId: '', expiresAt: '' }); // clears for rapid entry
      loadUnlinkedCount();
    } catch (e) {
      setMsg({ text: e.response?.data?.message || e.message || 'Failed to add subscriber', type: 'error' });
    } finally { setSaving(false); }
  };

  // Add Existing Subscriber — CSV bulk (paste/file preview-then-confirm, same pattern as voucher stock)
  const [inputMethod, setInputMethod] = useState('paste');
  const [rawText, setRawText] = useState('');
  const [fileName, setFileName] = useState('');
  const [parsedRows, setParsedRows] = useState([]);
  const [csvPreview, setCsvPreview] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [bulkResults, setBulkResults] = useState(null); // [{phone, name, ispIdentity, referenceCode}] from last bulk import

  const onTextChange = (text) => {
    setRawText(text);
    setParsedRows(parseSubscriberCsv(text));
    setCsvPreview(null);
  };

  const onFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setFileName(file.name);
    const reader = new FileReader();
    reader.onload = (ev) => {
      const text = ev.target.result || '';
      setParsedRows(parseSubscriberCsv(text));
      setRawText(text);
      setCsvPreview(null);
    };
    reader.readAsText(file);
  };

  const handlePreviewCsv = async () => {
    if (parsedRows.length === 0) return;
    setPreviewing(true);
    setMsg({ text: '', type: '' });
    try {
      const r = await ispPackageAPI.previewImportCsv(parsedRows);
      setCsvPreview(r);
    } catch (e) {
      setMsg({ text: 'Preview failed: ' + (e.response?.data?.message || e.message), type: 'error' });
    } finally { setPreviewing(false); }
  };

  const handleConfirmCsv = async () => {
    if (parsedRows.length === 0) return;
    setUploading(true);
    setMsg({ text: '', type: '' });
    setBulkResults(null);
    try {
      const r = await ispPackageAPI.bulkImport(parsedRows);
      setMsg({ text: `Added ${r.added} subscriber${r.added !== 1 ? 's' : ''}. ${r.duplicates} duplicate${r.duplicates !== 1 ? 's' : ''} skipped, ${r.invalid} invalid row${r.invalid !== 1 ? 's' : ''}.`, type: 'success' });
      setBulkResults(r.created || []);
      setRawText(''); setFileName(''); setParsedRows([]); setCsvPreview(null);
      loadUnlinkedCount();
    } catch (e) {
      setMsg({ text: e.response?.data?.message || e.message || 'Import failed', type: 'error' });
    } finally { setUploading(false); }
  };

  // Imported Subscribers list
  const [imported, setImported] = useState([]);
  const [importedLoading, setImportedLoading] = useState(false);
  const [linkedFilter, setLinkedFilter] = useState('');
  const [expiringSoon, setExpiringSoon] = useState('');

  const loadImported = useCallback(async () => {
    setImportedLoading(true);
    try {
      const r = await ispPackageAPI.adminImports({
        linked: linkedFilter || undefined,
        expiringSoonDays: expiringSoon || undefined,
      });
      setImported(r.docs || []);
    } catch (e) {
      setMsg({ text: e.response?.data?.message || e.message, type: 'error' });
    } finally { setImportedLoading(false); }
  }, [linkedFilter, expiringSoon]);

  useEffect(() => { if (inner === 'imported') loadImported(); }, [inner, loadImported]);

  // Edit dates — small modal, expiresAt + startDate only (identity fields aren't
  // editable here; startDate is historical/display only, doesn't drive anything).
  const [editingRecord, setEditingRecord] = useState(null); // { _id, expiresAt, startDate }
  const [savingDates, setSavingDates] = useState(false);

  const openEditDates = (s) => {
    setEditingRecord({
      _id: s._id,
      expiresAt: s.expiresAt ? new Date(s.expiresAt).toISOString().slice(0, 10) : '',
      startDate: s.startDate ? new Date(s.startDate).toISOString().slice(0, 10) : '',
    });
  };

  const handleSaveDates = async () => {
    if (!editingRecord) return;
    setSavingDates(true);
    setMsg({ text: '', type: '' });
    try {
      await ispPackageAPI.updateImportDates(editingRecord._id, {
        expiresAt: editingRecord.expiresAt || undefined,
        startDate: editingRecord.startDate || null,
      });
      setMsg({ text: 'Dates updated.', type: 'success' });
      setEditingRecord(null);
      loadImported();
    } catch (e) {
      setMsg({ text: e.response?.data?.message || e.message || 'Failed to update dates', type: 'error' });
    } finally { setSavingDates(false); }
  };

  // Unlinked-imports count — loaded independently of which inner tab is open,
  // so it's visible at a glance without having to switch tabs or set a filter.
  const [unlinkedCount, setUnlinkedCount] = useState(null);

  const loadUnlinkedCount = useCallback(async () => {
    try {
      const r = await ispPackageAPI.adminImports({ linked: 'false', limit: 1 });
      setUnlinkedCount(r.total || 0);
    } catch (e) { /* silent — non-critical stat */ }
  }, []);

  useEffect(() => { loadUnlinkedCount(); }, [loadUnlinkedCount]);

  return (
    <div style={{ padding: 16 }}>
      <h3 style={{ marginTop: 0, marginBottom: 12 }}>Activations — Monthly-Client Self-Service</h3>

      {unlinkedCount !== null && (
        <div
          onClick={() => { setInner('imported'); setLinkedFilter('false'); }}
          style={{
            display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer',
            background: unlinkedCount > 0 ? '#fef3c7' : '#dcfce7',
            color: unlinkedCount > 0 ? '#92400e' : '#166534',
            border: '1px solid ' + (unlinkedCount > 0 ? '#fde68a' : '#bbf7d0'),
            borderRadius: 8, padding: '8px 14px', marginBottom: 14, fontSize: 13, fontWeight: 700,
          }}
        >
          {unlinkedCount > 0
            ? `⚠ ${unlinkedCount} imported subscriber${unlinkedCount !== 1 ? 's' : ''} haven't joined the app yet — click to review`
            : '✓ All imported subscribers are linked to an app account'}
        </div>
      )}

      <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
        <button style={AS.innerTabBtn(inner === 'pending')} onClick={() => setInner('pending')}>
          Pending Activations {pending.length > 0 ? `(${pending.length})` : ''}
        </button>
        <button style={AS.innerTabBtn(inner === 'add')} onClick={() => setInner('add')}>Add Existing Subscriber</button>
        <button style={AS.innerTabBtn(inner === 'imported')} onClick={() => setInner('imported')}>
          Imported Subscribers {unlinkedCount > 0 ? `(${unlinkedCount} unlinked)` : ''}
        </button>
      </div>

      {msg.text && <div style={AS.msgBox(msg.type)}>{msg.text}</div>}

      {inner === 'pending' && (
        pendingLoading ? <p style={{ color: 'var(--text-muted)' }}>Loading…</p> :
        pending.length === 0 ? <p style={{ color: 'var(--text-muted)', fontStyle: 'italic' }}>No purchases awaiting activation.</p> :
        pending.map(p => {
          const waitingMin = (Date.now() - new Date(p.createdAt).getTime()) / 60000;
          const urgent = waitingMin > 15;
          return (
            <div key={p._id} style={urgent ? AS.rowUrgent : AS.row}>
              <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
                <div>
                  <strong>{p.userId?.name || 'Unknown customer'}</strong> &nbsp;
                  <span style={{ color: 'var(--text-muted)', fontSize: 13 }}>{p.userId?.phone}</span>
                  {urgent && <span style={AS.pill('#fee2e2', '#991b1b')}>OVERDUE</span>}
                  <div style={{ marginTop: 4, fontSize: 13 }}>
                    {p.packageId?.name || p.packageSnapshot?.name} — UGX {(p.amountUGX || 0).toLocaleString()}
                  </div>
                  <div style={{ marginTop: 4, fontSize: 13 }}>
                    ispIdentity: <strong style={{ color: '#22d3ee' }}>{p.userId?.ispIdentity || '—'}</strong>
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>
                    Paid {new Date(p.createdAt).toLocaleString()} — {Math.floor(waitingMin)} min ago
                  </div>
                </div>
                <button
                  style={AS.btn(activatingId === p.purchaseId, '#16a34a')}
                  disabled={activatingId === p.purchaseId}
                  onClick={() => handleActivate(p.purchaseId)}
                >
                  {activatingId === p.purchaseId ? 'Activating…' : 'Mark Activated'}
                </button>
              </div>
            </div>
          );
        })
      )}

      {inner === 'add' && (
        <div>
          <h4 style={{ marginBottom: 10 }}>Single Subscriber</h4>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 10 }}>
            <div><label style={AS.label}>Phone *</label>
              <input style={AS.input} value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} placeholder="0771234567" /></div>
            <div><label style={AS.label}>Name</label>
              <input style={AS.input} value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} /></div>
            <div><label style={AS.label}>DWIFI ispIdentity *</label>
              <input style={AS.input} value={form.ispIdentity} onChange={e => setForm(f => ({ ...f, ispIdentity: e.target.value }))} placeholder="ORANGE1-1234" /></div>
            <div><label style={AS.label}>Package</label>
              <select style={AS.sel} value={form.packageId} onChange={e => setForm(f => ({ ...f, packageId: e.target.value }))}>
                <option value="">— none —</option>
                {packages.map(p => <option key={p._id} value={p._id}>{p.name}</option>)}
              </select></div>
            <div><label style={AS.label}>Current expiry *</label>
              <input style={AS.input} type="date" value={form.expiresAt} onChange={e => setForm(f => ({ ...f, expiresAt: e.target.value }))} /></div>
          </div>
          <button style={AS.btn(saving)} disabled={saving} onClick={handleAddSubscriber}>
            {saving ? 'Adding…' : 'Add Subscriber'}
          </button>

          {addedCode && (
            <div style={{ marginTop: 14, background: '#0f172a', border: '1px solid #22d3ee', borderRadius: 10, padding: '14px 18px' }}>
              <p style={{ margin: '0 0 6px', fontSize: 12, color: '#94a3b8' }}>Subscriber added. Give this reference code to them (SMS/WhatsApp/verbally):</p>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <span style={{ fontFamily: 'monospace', fontSize: 22, fontWeight: 800, color: '#22d3ee', letterSpacing: '0.08em' }}>{addedCode}</span>
                <button
                  onClick={() => navigator.clipboard.writeText(addedCode).catch(() => {})}
                  style={{ padding: '5px 12px', borderRadius: 6, border: '1px solid #22d3ee', background: 'transparent', color: '#22d3ee', cursor: 'pointer', fontSize: 12 }}
                >Copy</button>
                <button
                  onClick={() => setAddedCode(null)}
                  style={{ padding: '5px 12px', borderRadius: 6, border: 'none', background: 'transparent', color: '#94a3b8', cursor: 'pointer', fontSize: 12 }}
                >Dismiss</button>
              </div>
            </div>
          )}

          <h4 style={{ margin: '24px 0 10px' }}>Bulk CSV Import</h4>
          <p style={{ fontSize: 12, color: 'var(--text-muted)', margin: '0 0 10px' }}>
            Columns: <code>phone,name,ispIdentity,packageName,expiryDate</code> (header row required)
          </p>
          <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
            <button style={AS.innerTabBtn(inputMethod === 'paste')} onClick={() => { setInputMethod('paste'); setCsvPreview(null); }}>Paste CSV</button>
            <button style={AS.innerTabBtn(inputMethod === 'file')} onClick={() => { setInputMethod('file'); setCsvPreview(null); }}>Upload File (.csv)</button>
          </div>
          {inputMethod === 'paste' && (
            <textarea
              value={rawText}
              onChange={e => onTextChange(e.target.value)}
              placeholder={'phone,name,ispIdentity,packageName,expiryDate\n0771234567,Jane,ORANGE1-1234,Orange Monthly,2026-08-15'}
              style={AS.ta}
            />
          )}
          {inputMethod === 'file' && (
            <div style={{ marginBottom: 12 }}>
              <input type="file" accept=".csv,text/csv,text/plain" onChange={onFileChange}
                style={{ display: 'block', marginBottom: 8, fontSize: 13, color: 'var(--text-primary)' }} />
              {fileName && <p style={{ fontSize: 12, color: 'var(--text-muted)', margin: 0 }}>{fileName} — {parsedRows.length} row{parsedRows.length !== 1 ? 's' : ''} detected</p>}
            </div>
          )}
          {parsedRows.length > 0 && !csvPreview && (
            <p style={{ fontSize: 12, color: 'var(--text-muted)', margin: '8px 0' }}>
              <strong style={{ color: 'var(--text-primary)' }}>{parsedRows.length}</strong> row{parsedRows.length !== 1 ? 's' : ''} detected — click Preview.
            </p>
          )}
          {csvPreview && (
            <div style={{ background: 'var(--bg-panel)', border: '1px solid var(--border)', borderRadius: 10, padding: '12px 16px', marginBottom: 12 }}>
              <p style={{ margin: '0 0 10px', fontWeight: 700, fontSize: 13 }}>Import Preview</p>
              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                {[
                  ['Will Add', csvPreview.valid, '#16a34a'],
                  ['Already Imported', csvPreview.dupInStock, '#d97706'],
                  ['Batch Dup', csvPreview.dupWithinBatch, '#6366f1'],
                  ['Invalid', csvPreview.invalid, '#dc2626'],
                ].map(([label, count, color]) => (
                  <div key={label} style={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'center', background: color + '22', borderRadius: 8, padding: '6px 14px', minWidth: 72, border: '1px solid ' + color + '44' }}>
                    <span style={{ fontSize: 18, fontWeight: 800, color }}>{count}</span>
                    <span style={{ fontSize: 10, fontWeight: 700, color, textTransform: 'uppercase' }}>{label}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
          <div style={{ display: 'flex', gap: 10, marginTop: 12 }}>
            <button style={AS.btn(previewing || parsedRows.length === 0)} disabled={previewing || parsedRows.length === 0} onClick={handlePreviewCsv}>
              {previewing ? 'Checking…' : 'Preview'}
            </button>
            {csvPreview && csvPreview.valid > 0 && (
              <button style={AS.btn(uploading, '#16a34a')} disabled={uploading} onClick={handleConfirmCsv}>
                {uploading ? 'Importing…' : `Confirm Import (${csvPreview.valid})`}
              </button>
            )}
          </div>

          {bulkResults && bulkResults.length > 0 && (
            <div style={{ marginTop: 16 }}>
              <p style={{ fontSize: 13, fontWeight: 700, margin: '0 0 8px' }}>
                Reference codes — give each subscriber theirs (SMS/WhatsApp/verbally):
              </p>
              <div style={{ maxHeight: 300, overflowY: 'auto', border: '1px solid var(--border)', borderRadius: 8 }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                  <thead>
                    <tr style={{ background: 'var(--bg-panel)' }}>
                      <th style={{ textAlign: 'left', padding: '6px 10px' }}>Phone</th>
                      <th style={{ textAlign: 'left', padding: '6px 10px' }}>Name</th>
                      <th style={{ textAlign: 'left', padding: '6px 10px' }}>ispIdentity</th>
                      <th style={{ textAlign: 'left', padding: '6px 10px' }}>Reference Code</th>
                    </tr>
                  </thead>
                  <tbody>
                    {bulkResults.map((row, i) => (
                      <tr key={i} style={{ borderTop: '1px solid var(--border)' }}>
                        <td style={{ padding: '6px 10px' }}>{row.phone}</td>
                        <td style={{ padding: '6px 10px' }}>{row.name || '—'}</td>
                        <td style={{ padding: '6px 10px' }}>{row.ispIdentity}</td>
                        <td style={{ padding: '6px 10px', fontFamily: 'monospace', color: '#22d3ee', fontWeight: 700 }}>{row.referenceCode}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {inner === 'imported' && (
        <div>
          <div style={{ display: 'flex', gap: 10, marginBottom: 14 }}>
            <select style={AS.sel} value={linkedFilter} onChange={e => setLinkedFilter(e.target.value)}>
              <option value="">All</option>
              <option value="true">Linked</option>
              <option value="false">Not yet joined app</option>
            </select>
            <select style={AS.sel} value={expiringSoon} onChange={e => setExpiringSoon(e.target.value)}>
              <option value="">Any expiry</option>
              <option value="3">Expiring in 3 days</option>
              <option value="7">Expiring in 7 days</option>
              <option value="30">Expiring in 30 days</option>
            </select>
          </div>
          {importedLoading ? <p style={{ color: 'var(--text-muted)' }}>Loading…</p> :
          imported.length === 0 ? <p style={{ color: 'var(--text-muted)', fontStyle: 'italic' }}>No imported subscribers match this filter.</p> :
          imported.map(s => (
            <div key={s._id} style={AS.row}>
              <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
                <div>
                  <strong>{s.name || s.phone}</strong> &nbsp;
                  <span style={{ color: 'var(--text-muted)', fontSize: 13 }}>{s.phone}</span>
                  {s.linkedUserId
                    ? <span style={AS.pill('#dcfce7', '#166534')}>LINKED</span>
                    : <span style={AS.pill('#fef9c3', '#854d0e')}>NOT YET JOINED</span>}
                  <span style={AS.pill(s.status === 'active' ? '#dcfce7' : s.status === 'superseded' ? '#e0e7ff' : '#fee2e2',
                    s.status === 'active' ? '#166534' : s.status === 'superseded' ? '#3730a3' : '#991b1b')}>
                    {s.status.toUpperCase()}
                  </span>
                  <div style={{ marginTop: 4, fontSize: 13 }}>
                    {s.packageId?.name || s.packageNameSnapshot} — ispIdentity: {s.ispIdentity}
                  </div>
                  {s.referenceCode && (
                    <div style={{ marginTop: 4, fontSize: 13, fontFamily: 'monospace', color: '#22d3ee' }}>
                      Reference code: {s.referenceCode}
                    </div>
                  )}
                  <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>
                    {s.startDate && <>Started {new Date(s.startDate).toLocaleDateString()} — </>}
                    Expires {new Date(s.expiresAt).toLocaleDateString()}
                  </div>
                </div>
                <button
                  onClick={() => openEditDates(s)}
                  style={{ alignSelf: 'flex-start', padding: '5px 12px', borderRadius: 6, border: '1px solid var(--border)', background: 'transparent', color: 'var(--text-secondary)', cursor: 'pointer', fontSize: 12 }}
                >Edit dates</button>
              </div>
            </div>
          ))}
        </div>
      )}

      {editingRecord && (
        <div
          onClick={() => setEditingRecord(null)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 200, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{ background: 'var(--bg-card)', borderRadius: 12, padding: 20, width: '100%', maxWidth: 340, border: '1px solid var(--border)' }}
          >
            <h4 style={{ margin: '0 0 14px' }}>Edit dates</h4>
            <label style={AS.label}>Expiry date</label>
            <input type="date" style={{ ...AS.input, width: '100%', marginBottom: 12 }}
              value={editingRecord.expiresAt}
              onChange={e => setEditingRecord(r => ({ ...r, expiresAt: e.target.value }))} />
            <label style={AS.label}>Start date <span style={{ color: 'var(--text-muted)' }}>(historical reference only — doesn't affect expiry)</span></label>
            <input type="date" style={{ ...AS.input, width: '100%', marginBottom: 16 }}
              value={editingRecord.startDate}
              onChange={e => setEditingRecord(r => ({ ...r, startDate: e.target.value }))} />
            <div style={{ display: 'flex', gap: 10 }}>
              <button style={AS.btn(savingDates, '#16a34a')} disabled={savingDates} onClick={handleSaveDates}>
                {savingDates ? 'Saving…' : 'Save'}
              </button>
              <button
                onClick={() => setEditingRecord(null)}
                style={{ padding: '9px 20px', borderRadius: 8, border: '1px solid var(--border)', background: 'transparent', color: 'var(--text-secondary)', cursor: 'pointer' }}
              >Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
